/**
 * Which connection target backs a saved Environment record, and how it is built.
 *
 * Two shapes reach this module. A Cloud record with a currently resolvable
 * runtime id gets a renewing CMMD credential provider, which mints a fresh
 * runtime ticket whenever the old one expires. Everything else gets a static
 * provider built from the record's own stored URLs and bearer token. Picking
 * between them is `resolveCmmdRuntimeId` returning a string or null, so the
 * decision lives next to the two things it decides between rather than being
 * re-derived at each call site.
 */
import { sanitizeSavedEnvironmentWsBaseUrl } from "@cmmd-forge/contracts";

import { fetchRemoteSessionState } from "../remote/api";
import { classifyEnvironmentId } from "../environmentIdClassification";
import { readCmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import { useCloudRuntimeProjectHandoffStore } from "../../components/sidebar/cloudRuntimeProjectHandoffStore";
import { findCloudRowForEnvironment } from "../../routes/cloudRouteCatalogRow";
import {
  persistSavedEnvironmentRecord,
  removeSavedEnvironmentBearerToken,
  type SavedEnvironmentRecord,
  useSavedEnvironmentRegistryStore,
} from "./catalog";
import {
  createCloudRuntimeCredentialProvider,
  type CloudRuntimeConnectionDescriptor,
  type CloudRuntimeCredentialProvider,
} from "./cloudRuntimeCredentialProvider";
import {
  registerCloudRuntimeCredentialProvider,
  type SavedEnvironmentConnectionTarget,
  type SavedEnvironmentConnectionTargetProvider,
} from "./cloudRuntimeCredentialAccess";
import { mergeSavedEnvironmentCapability } from "./savedCloudEnvironmentLifecycle";

/**
 * The current runtime id backing a Cloud record, or null when none is
 * resolvable right now.
 *
 * A legacy `cmmd-runtime:{id}` id carries its runtime id in the id itself. A
 * durable `env_...` id does not: the Sprite currently backing it (if any) has
 * to come from the live catalog, since a durable Environment can be asleep
 * with no Sprite at all. Null here does not mean "not cloud", it means "no
 * renewing provider is available right now"; callers fall back to whatever a
 * non-cloud record would do instead of treating it as an error.
 */
export function resolveCmmdRuntimeId(record: SavedEnvironmentRecord): string | null {
  const runtimeId = record.runtimeId?.trim();
  if (runtimeId) return runtimeId;
  const legacyRuntimeId = classifyEnvironmentId(record.environmentId).legacyRuntimeId;
  if (legacyRuntimeId) return legacyRuntimeId;
  const catalogRows = readCmmdForgeRuntimeListSnapshot().rows;
  const handoffs = Object.values(useCloudRuntimeProjectHandoffStore.getState().byRuntimeId);
  return findCloudRowForEnvironment(record.environmentId, catalogRows, handoffs)?.runtimeId ?? null;
}

export function createCmmdRuntimeTargetProvider(
  record: SavedEnvironmentRecord,
  runtimeId: string,
  initialBearerToken?: string,
  canCommit?: () => boolean,
): CloudRuntimeCredentialProvider {
  let currentRecord = record;
  const provider = createCloudRuntimeCredentialProvider({
    runtimeId,
    ...(record.cmmdOrganizationId === undefined
      ? {}
      : { organizationId: record.cmmdOrganizationId }),
    ...(initialBearerToken === undefined
      ? {}
      : {
          initialDescriptor: {
            httpBaseUrl: record.httpBaseUrl,
            wsBaseUrl: record.wsBaseUrl,
            bearerToken: initialBearerToken,
            expiresAt: record.runtimeTicketExpiresAt ?? null,
          },
        }),
    validateDescriptor: async (descriptor) => {
      await fetchRemoteSessionState({
        httpBaseUrl: descriptor.httpBaseUrl,
        bearerToken: descriptor.bearerToken,
      });
    },
    persistDescriptor: async (descriptor: CloudRuntimeConnectionDescriptor) => {
      if (canCommit && !canCommit()) {
        throw new Error("Saved environment changed while its connection was being acquired.");
      }
      const refreshedRecord: SavedEnvironmentRecord = {
        ...mergeSavedEnvironmentCapability(currentRecord),
        httpBaseUrl: descriptor.httpBaseUrl,
        wsBaseUrl: sanitizeSavedEnvironmentWsBaseUrl(descriptor.wsBaseUrl),
        ...(descriptor.expiresAt === null ? {} : { runtimeTicketExpiresAt: descriptor.expiresAt }),
        lastConnectedAt: new Date().toISOString(),
      };
      await persistSavedEnvironmentRecord(refreshedRecord);
      await removeSavedEnvironmentBearerToken(refreshedRecord.environmentId);
      useSavedEnvironmentRegistryStore.getState().upsert(refreshedRecord);
      currentRecord = refreshedRecord;
    },
  });
  registerCloudRuntimeCredentialProvider(record.environmentId, provider);
  return provider;
}

export function createStaticSavedEnvironmentTargetProvider(input: {
  readonly record: SavedEnvironmentRecord;
  readonly bearerToken: string;
}): SavedEnvironmentConnectionTargetProvider {
  const target: SavedEnvironmentConnectionTarget = {
    wsBaseUrl: input.record.wsBaseUrl,
    httpBaseUrl: input.record.httpBaseUrl,
    bearerToken: input.bearerToken,
    expiresAt: input.record.runtimeTicketExpiresAt ?? null,
  };
  return { acquire: () => Promise.resolve(target) };
}
